/** Brand accents sampled from the OllaCart logo (teal wheel + coral cart). */
export const DEFAULT_THEME = {
  accent: "#069cb4",
  accent2: "#f94c4b",
};

export const THEME_PRESETS = [
  { id: "ollacart", label: "OllaCart", accent: "#069cb4", accent2: "#f94c4b" },
  { id: "brand-blue", label: "Brand Blue", accent: "#16459c", accent2: "#069cb4" },
  { id: "neon", label: "Neon", accent: "#00d4ff", accent2: "#7c3aed" },
  { id: "ocean", label: "Ocean", accent: "#22d3ee", accent2: "#3b82f6" },
  { id: "sunset", label: "Sunset", accent: "#fb923c", accent2: "#e11d48" },
  { id: "forest", label: "Forest", accent: "#34d399", accent2: "#059669" },
];

const STORAGE_KEY = "ollacart_theme_v2";

function hexToRgb(hex) {
  const h = String(hex || "").replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function isValidHex(hex) {
  return typeof hex === "string" && /^#[0-9A-Fa-f]{6}$/.test(hex);
}

export function normalizeTheme(theme) {
  const accent = isValidHex(theme?.accent)
    ? theme.accent.toLowerCase()
    : DEFAULT_THEME.accent;
  const accent2 = isValidHex(theme?.accent2)
    ? theme.accent2.toLowerCase()
    : DEFAULT_THEME.accent2;
  return { accent, accent2 };
}

export function applyThemeColors(theme) {
  const { accent, accent2 } = normalizeTheme(theme);
  const a = hexToRgb(accent);
  const b = hexToRgb(accent2);
  const root = document.documentElement;

  root.style.setProperty("--accent", accent);
  root.style.setProperty(
    "--accent-dim",
    `rgba(${a.r}, ${a.g}, ${a.b}, 0.12)`
  );
  root.style.setProperty(
    "--accent-glow",
    `0 0 24px rgba(${a.r}, ${a.g}, ${a.b}, 0.28)`
  );
  root.style.setProperty(
    "--accent-glow-sm",
    `0 0 10px rgba(${a.r}, ${a.g}, ${a.b}, 0.2)`
  );
  root.style.setProperty("--accent2", accent2);
  root.style.setProperty(
    "--accent2-dim",
    `rgba(${b.r}, ${b.g}, ${b.b}, 0.12)`
  );
  root.style.setProperty(
    "--accent2-glow",
    `0 0 24px rgba(${b.r}, ${b.g}, ${b.b}, 0.28)`
  );
  root.style.setProperty("--color-info", accent);
  root.style.setProperty("--admin-primary", accent);
  root.style.setProperty("--admin-secondary", accent2);

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ accent, accent2 }));
  } catch (_) {
    /* ignore */
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("ollacart-theme", { detail: { accent, accent2 } })
    );
  }

  return { accent, accent2 };
}

export function loadStoredTheme() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return normalizeTheme(JSON.parse(raw));
  } catch (_) {
    return null;
  }
}

export function initThemeFromStorage() {
  const stored = loadStoredTheme();
  if (stored) applyThemeColors(stored);
  return stored;
}

export function createMuiThemeFromColors(createTheme, colors) {
  const { accent, accent2 } = normalizeTheme(colors);
  return createTheme({
    palette: {
      type: "dark",
      primary: {
        main: accent,
        contrastText: "#04040a",
      },
      secondary: {
        main: accent2,
        contrastText: "#e8eaf6",
      },
      error: { main: "#ff3d71" },
      warning: { main: "#fcd66a" },
      success: { main: "#00e5a0" },
      background: {
        default: "#04040a",
        paper: "#0c0c1a",
      },
      text: {
        primary: "#e8eaf6",
        secondary: "rgba(232,234,246,0.55)",
      },
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: "'Roboto', sans-serif",
    },
  });
}
